// ABOUTME: Main authenticated app shell that renders the dashboard UI after login.
// ABOUTME: Manages dashboard state, link/widget CRUD, and layout.
import React, { useMemo, useCallback, useEffect, useRef } from "react";
import { Routes, Route } from "react-router-dom";
import { User } from "firebase/auth";
import { useDashboard } from "../hooks/useDashboard";
import { usePrefsSync } from "../hooks/usePrefsSync";
import { DashboardItem, dashboardService } from "../services/dashboardService";
import { UrlData } from "../services/firebase";
import { limitsService } from "../services/limitsService";
import { backfillLinkTitle } from "../services/linkTitleService";
import { isUnnamed } from "../utils/linkLabel";
import { useDashboardStore } from "../stores/dashboard";
import { reflowForColumnCount } from "../utils/layoutReflow";
import { useUIStore } from "../stores/ui";
import { useAuthStore } from "../stores/auth";

import StartDashboard from "./StartDashboard";
import Header from "./Header";
import AddDashboardSheet from "./AddDashboardSheet";
import Settings from "./Settings";
import ErrorToast from "./ErrorToast";
import SignInModal from "./SignInModal";
import SignUpModal from "./SignUpModal";

interface AuthenticatedAppProps {
  user: User;
  signOut: () => void;
  isSignInModalOpen: boolean;
  setSignInModalOpen: (open: boolean) => void;
  isSignUpModalOpen: boolean;
  setSignUpModalOpen: (open: boolean) => void;
  signInMessage: string;
  signInError: string;
  onSignUp: (data: {
    email: string;
    firstName: string;
    lastName: string;
    zip: string;
    ip?: string;
    inviteCode: string;
  }) => void;
}

function AuthenticatedApp({
  user,
  signOut,
  isSignInModalOpen,
  setSignInModalOpen,
  isSignUpModalOpen,
  setSignUpModalOpen,
  signInMessage,
  signInError,
  onSignUp,
}: AuthenticatedAppProps) {
  // The Customize sheet is mounted below and opened through the store.
  const openSheet = useDashboardStore((s) => s.openSheet);

  // Get column count from Zustand UI store
  const { columnCount, setColumnCount } = useUIStore();

  // Use the unified dashboard hook
  const {
    dashboardItems,
    loading: dashboardLoading,
    error: dashboardError,
    loadError: dashboardLoadError,
    clearError: clearDashboardError,
    addDashboardItem,
    updateDashboardItem,
    deleteDashboardItem,
    updateDashboardLayout,
  } = useDashboard(user?.uid);

  // A page title arrives after the add resolves; it is checked against the
  // items as they are then, not as this render saw them.
  const latestItems = useRef(dashboardItems);
  useEffect(() => {
    latestItems.current = dashboardItems;
  }, [dashboardItems]);

  // Theme, columns, spacing, link target and search engine follow the account.
  usePrefsSync(user?.uid);

  // Compute installedWidgets for modal
  const installedWidgets = useMemo(() => {
    return Object.fromEntries(
      dashboardItems.filter((i) => i.type === "widget").map((i) => [i.widgetName, true])
    );
  }, [dashboardItems]);

  // Dashboard handlers - use useCallback to prevent stale closures
  const handleAddLink = useCallback(
    async (newLink: {
      url: string;
      name?: string;
      tags?: string[];
      bgColor?: string;
      col?: number;
    }) => {
      if (!user?.uid) return;

      // Check user limits before adding
      const currentLinkCount = dashboardItems.filter((item) => item.type === "link").length;
      if (!limitsService.canAddLink(currentLinkCount)) {
        alert(limitsService.getLimitMessage("maxLinks"));
        return;
      }

      const col = typeof newLink.col === "number" ? newLink.col : 0;
      const order = dashboardItems.filter((i) => i.type === "link" && i.col === col).length;

      try {
        const id = await addDashboardItem({
          type: "link",
          url: newLink.url,
          name: newLink.name || newLink.url,
          tags: newLink.tags || [],
          ...(newLink.bgColor ? { bgColor: newLink.bgColor } : {}),
          col,
          order,
          createdDate: Date.now(),
        });
        if (isUnnamed(newLink)) {
          backfillLinkTitle(id, newLink.url, {
            getItem: (itemId) => latestItems.current.find((i) => i.id === itemId),
            update: updateDashboardItem,
          }).catch((error) => console.warn("Link title did not apply:", error));
        }
      } catch (error) {
        // Surfaced by the ErrorToast below via the hook's error state.
        console.error("Error adding link:", error);
      }
    },
    [user, dashboardItems, addDashboardItem, updateDashboardItem]
  );

  const handleWidgetInstall = useCallback(
    async (name: string) => {
      if (!user?.uid) return;

      if (dashboardItems.some((i) => i.type === "widget" && i.widgetName === name)) return;

      // Check user limits before adding widget
      const currentWidgetCount = dashboardItems.filter((item) => item.type === "widget").length;
      if (!limitsService.canAddWidget(currentWidgetCount)) {
        alert(limitsService.getLimitMessage("maxWidgets"));
        return;
      }

      const colCounts = [0, 1, 2].map((col) => dashboardItems.filter((i) => i.col === col).length);
      const col = colCounts.indexOf(Math.min(...colCounts));
      const order = dashboardItems.filter((i) => i.col === col).length;

      await addDashboardItem({
        type: "widget",
        widgetName: name,
        col,
        order,
      });
    },
    [user, dashboardItems, addDashboardItem]
  );

  const handleWidgetRemove = async (name: string) => {
    if (!user?.uid) return;

    const widgetToRemove = dashboardItems.find((i) => i.type === "widget" && i.widgetName === name);
    if (widgetToRemove) {
      await deleteDashboardItem(widgetToRemove.id);
    }
  };

  const handleDeleteItem = async (id: string) => {
    if (!user?.uid) return;
    await deleteDashboardItem(id);
  };

  const handleEditLink = async (id: string, updated: Partial<UrlData>) => {
    if (!user?.uid) return;
    await updateDashboardItem(id, updated);
  };

  const uid = user?.uid;
  // Settings owns the confirmation and the success/failure message.
  const handleDeleteAllLinks = useCallback(async () => {
    if (!uid) throw new Error("User not authenticated");
    await dashboardService.deleteAllLinks(uid);
  }, [uid]);

  const handleImportItems = useCallback(
    async (items: Omit<DashboardItem, "id">[]) => {
      if (!uid) throw new Error("User not authenticated");
      await dashboardService.addDashboardItems(uid, items);
    },
    [uid]
  );

  // Lets the Suggested tab mark links the user already has.
  const existingUrls = useMemo(
    () => dashboardItems.filter((i) => i.type === "link" && i.url).map((i) => i.url as string),
    [dashboardItems]
  );

  const handleChangeColumns = (count: number) => {
    // Check user limits before changing columns
    if (!limitsService.canSetColumns(count)) {
      alert(limitsService.getLimitMessage("maxColumns"));
      return;
    }

    setColumnCount(count);

    // Preserves the existing arrangement — only items in columns being removed
    // move. A no-op when growing, so grow/shrink round-trips cleanly.
    const newItems = reflowForColumnCount(dashboardItems, count);
    if (newItems !== dashboardItems) {
      // Failure is shown by the ErrorToast; the catch stops it being an
      // unhandled rejection.
      updateDashboardLayout(newItems).catch((err) => {
        console.error("Column change failed:", err);
      });
    }
  };

  // Read admin status from the central auth store (set once during login)
  const isAdmin = useAuthStore((s) => s.isAdmin);

  return (
    <div className="min-h-screen bg-[var(--color-bg)] transition-colors">
      {signInMessage && (
        <div
          role="status"
          className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-md w-[calc(100%-2rem)] text-center bg-[var(--color-bg-secondary)] border border-[color-mix(in_srgb,var(--color-success)_40%,transparent)] text-[var(--color-text-primary)] text-sm px-4 py-2 rounded-[var(--radius-md)] shadow-lg"
        >
          {signInMessage}
        </div>
      )}
      {signInError && (
        <div
          role="alert"
          className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-md w-[calc(100%-2rem)] text-center bg-[var(--color-bg-secondary)] border border-[color-mix(in_srgb,var(--color-danger)_40%,transparent)] text-[var(--color-danger)] text-sm px-4 py-2 rounded-[var(--radius-md)] shadow-lg"
        >
          {signInError}
        </div>
      )}

      <Header
        user={user ? { email: user.email ?? "", photoURL: user.photoURL ?? undefined } : null}
        items={dashboardItems}
        onAddClick={() => openSheet("Links")}
        onSignOut={signOut}
        isAdmin={isAdmin}
        className="sticky top-0 z-40"
      />

      <main className="mx-auto max-w-7xl p-0 lg:px-8">
        <Routes>
          <Route
            path="/"
            element={
              <StartDashboard
                dashboardItems={dashboardItems}
                setDashboardItems={updateDashboardLayout}
                onWidgetRemove={handleWidgetRemove}
                onDeleteItem={handleDeleteItem}
                onEditLink={handleEditLink}
                loading={dashboardLoading}
                loadError={dashboardLoadError}
                columnCount={columnCount}
              />
            }
          />
          <Route
            path="/settings"
            element={
              <Settings
                dashboardItems={dashboardItems}
                onImport={handleImportItems}
                onDeleteAllLinks={handleDeleteAllLinks}
              />
            }
          />
        </Routes>
      </main>

      <ErrorToast message={dashboardError?.message} onDismiss={clearDashboardError} />

      <SignInModal isOpen={isSignInModalOpen} onClose={() => setSignInModalOpen(false)} />
      <AddDashboardSheet
        onAddLink={(link) => handleAddLink(link)}
        loading={dashboardLoading}
        onWidgetInstall={handleWidgetInstall}
        onWidgetRemove={handleWidgetRemove}
        installedWidgets={installedWidgets}
        existingUrls={existingUrls}
        columnCount={columnCount}
        onChangeColumns={handleChangeColumns}
      />
      <SignUpModal
        isOpen={isSignUpModalOpen}
        onClose={() => setSignUpModalOpen(false)}
        onSubmit={onSignUp}
      />
    </div>
  );
}

export default AuthenticatedApp;
